// Tabelle, Karten, Seitenwahl und Auswahl-Leiste der Ticket-Liste (Handoff §1).
import type { KeyboardEvent, ReactNode } from 'react';
import { ArrowCounterClockwise, CaretDown, CaretLeft, CaretRight, CaretUp, Check, DotsThree, Fire, Trash, X } from '@phosphor-icons/react';
import { MenueEintrag, Popover } from '../../../../components/immobilien/ui';
import { faelligListe, type FaelligTon } from '../../../../lib/tickets/darstellung';
import { KleinAvatar, OPTION_STIL, TicketPrioPill, TicketStatusPill, prioFarbe, statusFarbe } from './teile';

/** Was Tabelle und Karten von einem Ticket brauchen. */
export interface ListenTicket {
  id: string;
  ticket_nummer: string;
  titel: string;
  status: string;
  prioritaet: string;
  faellig_am: string | null;
  eskaliert: boolean;
  geloescht_am?: string | null;
  created_at: string;
  kategorie?: { name: string } | null;
  objekt?: { bezeichnung: string } | null;
  zustaendig?: { full_name: string; isTeam?: boolean } | null;
}

export type SortSchluessel = 'ticket_nummer' | 'titel' | 'status' | 'prioritaet' | 'kategorie' | 'objekt' | 'zustaendig' | 'created_at' | 'faellig_am';

const FAELLIG_STIL: Record<FaelligTon, { color: string; fontWeight: number }> = {
  leer: { color: 'var(--ink3)', fontWeight: 500 },
  abgeschlossen: { color: 'var(--ink3)', fontWeight: 600 },
  ueberfaellig: { color: 'var(--bad)', fontWeight: 800 },
  heute: { color: 'var(--warn)', fontWeight: 800 },
  morgen: { color: 'var(--ink)', fontWeight: 700 },
  normal: { color: 'var(--ink2)', fontWeight: 600 },
};

export const datumKurz = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function Haken({ an, onClick, label, tip }: { an: boolean; onClick: () => void; label: string; tip: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={an} aria-label={label} data-tip={tip}
      onClick={e => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center justify-center"
      style={{ width: 20, height: 20, borderRadius: 6, border: 'none', cursor: 'pointer', padding: 0,
        background: an ? 'var(--acc-fill)' : 'var(--fill2)', boxShadow: an ? 'var(--acc-sh)' : 'inset 0 0 0 1.5px var(--line)', color: 'var(--acc-ink)' }}>
      {an && <Check size={12} weight="bold" />}
    </button>
  );
}

/** Ticket öffnen per Enter/Leertaste auf der Zeile. */
const oeffnenTaste = (oeffnen: () => void) => (e: KeyboardEvent) => {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); oeffnen(); }
};

function ZeilenMenue({ papierkorb, onOeffnen, onArchiv }: { papierkorb: boolean; onOeffnen: () => void; onArchiv: () => void }) {
  return (
    <Popover label="Weitere Aktionen" breite={200} ausloeser={({ offen, umschalten, ref }) => (
      <button ref={ref} type="button" aria-label="Weitere Aktionen" aria-expanded={offen} aria-haspopup="menu"
        data-tip="Weitere Aktionen – Öffnen oder in den Papierkorb"
        onClick={e => { e.stopPropagation(); umschalten(); }}
        className="inline-flex items-center justify-center"
        style={{ width: 30, height: 30, borderRadius: 10, border: 'none', cursor: 'pointer', background: offen ? 'var(--fill2)' : 'transparent', color: 'var(--ink2)' }}>
        <DotsThree size={18} weight="bold" />
      </button>
    )}>
      {schliessen => (
        <div role="menu" className="flex flex-col" style={{ padding: 4 }}>
          <MenueEintrag onClick={() => { schliessen(); onOeffnen(); }}>Öffnen</MenueEintrag>
          <MenueEintrag gefahr={!papierkorb} onClick={() => { schliessen(); onArchiv(); }}>{papierkorb ? 'Wiederherstellen' : 'Archivieren'}</MenueEintrag>
        </div>
      )}
    </Popover>
  );
}

export interface TicketTabelleProps {
  tickets: readonly ListenTicket[];
  heute: string;
  spalten: { objekt: boolean; erstellt: boolean };
  sortKey: SortSchluessel; sortDir: 'asc' | 'desc'; onSort: (k: SortSchluessel) => void;
  ausgewaehlt: ReadonlySet<string>; onAuswahl: (id: string) => void;
  alleAufSeite: boolean; onAlleAufSeite: () => void;
  papierkorb: boolean;
  entwurfIds: ReadonlySet<string>;
  onOeffnen: (id: string) => void;
  onArchiv: (id: string) => void;
}

export function TicketTabelle(p: TicketTabelleProps) {
  const grid = `20px minmax(180px,2.2fr) ${p.spalten.objekt ? 'minmax(110px,1fr) ' : ''}minmax(110px,1fr) 92px 128px 128px ${p.spalten.erstellt ? '92px ' : ''}30px`;
  const kopf: { key: SortSchluessel; label: string; zeigen: boolean }[] = [
    { key: 'titel', label: 'Ticket', zeigen: true },
    { key: 'objekt', label: 'Objekt', zeigen: p.spalten.objekt },
    { key: 'zustaendig', label: 'Zuständig', zeigen: true },
    { key: 'prioritaet', label: 'Priorität', zeigen: true },
    { key: 'status', label: 'Status', zeigen: true },
    { key: 'faellig_am', label: 'Fällig', zeigen: true },
    { key: 'created_at', label: 'Erstellt', zeigen: p.spalten.erstellt },
  ];
  return (
    <div role="table" aria-label="Tickets" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: '6px 8px', overflow: 'hidden' }}>
      <div role="row" className="grid items-center" style={{ gridTemplateColumns: grid, gap: 12, padding: '10px 10px 8px', borderBottom: '1px solid var(--line)' }}>
        <span role="columnheader"><Haken an={p.alleAufSeite} onClick={p.onAlleAufSeite} label="Alle markieren" tip="Alle markieren – Alle Tickets dieser Seite auswählen" /></span>
        {kopf.filter(k => k.zeigen).map(k => {
          const aktiv = p.sortKey === k.key;
          return (
            <span key={k.key} role="columnheader" aria-sort={aktiv ? (p.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
              <button type="button" onClick={() => p.onSort(k.key)} data-tip={`${k.label} – Nach dieser Spalte sortieren (nochmal klicken = umkehren)`}
                className="inline-flex items-center" style={{ gap: 4, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: aktiv ? 'var(--ink)' : 'var(--ink3)' }}>
                {k.label}
                {aktiv && (p.sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
              </button>
            </span>
          );
        })}
        <span role="columnheader" aria-label="Aktionen" />
      </div>
      {p.tickets.length === 0 ? (
        <div role="row" className="flex items-center justify-center" style={{ padding: '48px 0', fontSize: 14, fontWeight: 600, color: 'var(--ink3)' }}>
          <span role="cell">Keine Tickets gefunden</span>
        </div>
      ) : p.tickets.map(t => {
        const f = faelligListe(t.faellig_am, t.status, p.heute);
        const unter = [t.ticket_nummer, t.kategorie?.name, !p.spalten.objekt ? t.objekt?.bezeichnung : null, p.entwurfIds.has(t.id) ? 'Entwurf' : null].filter(Boolean).join(' · ');
        const oeffnen = () => p.onOeffnen(t.id);
        return (
          <div key={t.id} role="row" tabIndex={0} onClick={oeffnen} onKeyDown={oeffnenTaste(oeffnen)} aria-label={`${t.ticket_nummer} ${t.titel}`}
            data-tip="Ticket öffnen – Detailansicht mit Verlauf und Einsatz"
            className="immo-tzeile grid cursor-pointer items-center"
            style={{ gridTemplateColumns: grid, gap: 12, padding: '8px 10px', borderRadius: 16, background: p.ausgewaehlt.has(t.id) ? 'var(--acc-soft)' : undefined }}>
            <span role="cell"><Haken an={p.ausgewaehlt.has(t.id)} onClick={() => p.onAuswahl(t.id)} label={`${t.ticket_nummer} markieren`} tip="Markieren – Für Sammelaktionen auswählen" /></span>
            <span role="cell" className="flex min-w-0 items-center" style={{ gap: 10 }}>
              <span aria-hidden style={{ width: 4, height: 34, borderRadius: 2, background: prioFarbe(t.prioritaet), flexShrink: 0 }} />
              <span className="flex min-w-0 flex-col" style={{ gap: 2 }}>
                <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
                  {t.eskaliert && <Fire size={13} weight="fill" color="#a24bc9" aria-label="Eskaliert" />}
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{t.titel}</span>
                </span>
                <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>{unter}</span>
              </span>
            </span>
            {p.spalten.objekt && <span role="cell" className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>{t.objekt?.bezeichnung ?? '—'}</span>}
            <span role="cell" className="flex min-w-0 items-center" style={{ gap: 8 }}>
              {t.zustaendig ? <>
                <KleinAvatar name={t.zustaendig.full_name} team={t.zustaendig.isTeam} />
                <span className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>{t.zustaendig.full_name}</span>
              </> : <span style={{ fontSize: 13, color: 'var(--ink3)' }}>—</span>}
            </span>
            <span role="cell"><TicketPrioPill prio={t.prioritaet} /></span>
            <span role="cell"><TicketStatusPill status={t.status} /></span>
            <span role="cell" className="truncate tabular-nums" style={{ fontSize: 12.5, ...FAELLIG_STIL[f.ton] }}>{f.text}</span>
            {p.spalten.erstellt && <span role="cell" className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>{datumKurz(t.created_at)}</span>}
            <span role="cell" onClick={e => e.stopPropagation()}>
              <ZeilenMenue papierkorb={p.papierkorb} onOeffnen={oeffnen} onArchiv={() => p.onArchiv(t.id)} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Karten (schmale Breiten): Nr · Datum, Status, Titel, Prio, Objekt, Fällig, Zuständig. */
export function TicketKarten({ tickets, heute, entwurfIds, onOeffnen }: { tickets: readonly ListenTicket[]; heute: string; entwurfIds: ReadonlySet<string>; onOeffnen: (id: string) => void }) {
  if (tickets.length === 0) {
    return <div className="immo-glas flex items-center justify-center" style={{ borderRadius: 22, padding: '40px 0', fontSize: 14, fontWeight: 600, color: 'var(--ink3)' }}>Keine Tickets</div>;
  }
  return (
    <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))' }}>
      {tickets.map(t => {
        const f = faelligListe(t.faellig_am, t.status, heute);
        const oeffnen = () => onOeffnen(t.id);
        return (
          <div key={t.id} role="button" tabIndex={0} onClick={oeffnen} onKeyDown={oeffnenTaste(oeffnen)} aria-label={`${t.ticket_nummer} ${t.titel} öffnen`}
            data-tip="Ticket öffnen – Detailansicht mit Verlauf und Einsatz"
            className="immo-glas immo-tkarte flex min-w-0 cursor-pointer flex-col"
            style={{ borderRadius: 22, padding: '14px 14px 14px 16px', gap: 8, boxShadow: `var(--glass-sh), inset 4px 0 0 ${prioFarbe(t.prioritaet)}` }}>
            <span className="flex items-center justify-between" style={{ gap: 8 }}>
              <span className="flex min-w-0 items-center truncate" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
                {t.eskaliert && <Fire size={12} weight="fill" color="#a24bc9" aria-label="Eskaliert" />}
                {t.ticket_nummer} · {datumKurz(t.created_at)}{entwurfIds.has(t.id) ? ' · Entwurf' : ''}
              </span>
              <TicketStatusPill status={t.status} />
            </span>
            <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{t.titel}</span>
            <span className="flex items-center justify-between" style={{ gap: 8 }}>
              <span className="flex min-w-0 items-center" style={{ gap: 8 }}>
                <TicketPrioPill prio={t.prioritaet} />
                {t.objekt?.bezeichnung && <span className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>{t.objekt.bezeichnung}</span>}
              </span>
              <span className="flex shrink-0 items-center" style={{ gap: 8 }}>
                {f.ton !== 'leer' && <span className="tabular-nums" style={{ fontSize: 12.5, ...FAELLIG_STIL[f.ton] }}>{f.text}</span>}
                {t.zustaendig && <KleinAvatar name={t.zustaendig.full_name} team={t.zustaendig.isTeam} />}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SeitenKnopf({ children, aktiv = false, gesperrt = false, onClick, label }: { children: ReactNode; aktiv?: boolean; gesperrt?: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} aria-label={label} data-tip={label} aria-current={aktiv ? 'page' : undefined}
      className="inline-flex items-center justify-center tabular-nums"
      style={{ minWidth: 32, height: 32, padding: '0 8px', borderRadius: 10, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: aktiv ? 800 : 600,
        cursor: gesperrt ? 'default' : 'pointer', opacity: gesperrt ? 0.35 : 1,
        background: aktiv ? 'var(--acc-fill)' : 'transparent', color: aktiv ? 'var(--acc-ink)' : 'var(--ink2)', boxShadow: aktiv ? 'var(--acc-sh)' : 'none' }}>
      {children}
    </button>
  );
}

/** Fußzeile: „1–20 von N“, Seitenzahlen, 20/50/100 je Seite. */
export function Seitenwahl({ von, bis, gesamt, seite, seiten, nummern, onSeite, proSeite, onProSeite }: {
  von: number; bis: number; gesamt: number; seite: number; seiten: number; nummern: (number | '...')[];
  onSeite: (s: number) => void; proSeite: number; onProSeite: (n: number) => void;
}) {
  return (
    <nav aria-label="Seiten" className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: '4px 6px' }}>
      <span className="tabular-nums" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>{von}–{bis} von {gesamt}</span>
      <span className="flex items-center" style={{ gap: 2 }}>
        <SeitenKnopf label="Vorherige Seite" gesperrt={seite <= 1} onClick={() => onSeite(seite - 1)}><CaretLeft size={13} weight="bold" /></SeitenKnopf>
        {nummern.map((n, i) => n === '...'
          ? <span key={`l${i}`} aria-hidden style={{ padding: '0 4px', color: 'var(--ink3)' }}>…</span>
          : <SeitenKnopf key={n} label={`Seite ${n}`} aktiv={n === seite} onClick={() => onSeite(n)}>{n}</SeitenKnopf>)}
        <SeitenKnopf label="Nächste Seite" gesperrt={seite >= seiten} onClick={() => onSeite(seite + 1)}><CaretRight size={13} weight="bold" /></SeitenKnopf>
      </span>
      <select aria-label="Tickets pro Seite" value={proSeite} onChange={e => onProSeite(Number(e.target.value))} data-tip="Tickets pro Seite – Anzahl der Zeilen je Seite"
        style={{ height: 34, borderRadius: 11, border: 'none', padding: '0 10px', background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        {[20, 50, 100].map(n => <option style={OPTION_STIL} key={n} value={n}>{n}/Seite</option>)}
      </select>
    </nav>
  );
}

/** Leiste über der Tabelle, sobald Tickets markiert sind. */
export function AuswahlLeiste({ anzahl, papierkorb, laeuft, onAktion, onAufheben }: { anzahl: number; papierkorb: boolean; laeuft: boolean; onAktion: () => void; onAufheben: () => void }) {
  const label = papierkorb ? 'Wiederherstellen' : 'Archivieren';
  const I = papierkorb ? ArrowCounterClockwise : Trash;
  return (
    <div role="region" aria-label="Sammelaktion" className="immo-side-in flex flex-wrap items-center" style={{ gap: 10, padding: '8px 10px 8px 14px', borderRadius: 18, background: 'var(--acc-soft)' }}>
      <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{anzahl} ausgewählt</span>
      <button type="button" onClick={onAktion} disabled={laeuft} data-tip={`${label} – Aktion auf alle markierten Tickets anwenden`}
        className="inline-flex items-center" style={{ gap: 6, height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: laeuft ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
          background: papierkorb ? 'var(--ok-soft)' : 'var(--bad-soft)', color: papierkorb ? 'var(--ok)' : 'var(--bad)', opacity: laeuft ? 0.6 : 1 }}>
        <I size={14} weight="bold" />{label}
      </button>
      <button type="button" onClick={onAufheben} aria-label="Auswahl aufheben" data-tip="Auswahl aufheben" className="ml-auto inline-flex items-center justify-center"
        style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}

export const statusPunkt = statusFarbe;
